/**
 * `createReshapedView` — build a virtual view that is a pure reshape of one
 * source CDF view: a fixed `where` predicate plus column projection and
 * de-prefixing rename. It compiles the spec to a `rewrite`-mode
 * `DatasetProvider`, so the engine's pushdown, fuzzy matching, and schema
 * refinement all apply to the expansion for free — the factory itself owns
 * no source and no fetch.
 *
 * Column types and the agent-facing `ColumnDoc`s (canonical engine
 * `ColumnType`, with the unit baked into the leaf type) are DERIVED from the
 * source view's parsed schema; only the canonical name and its description are
 * authored (in the shared `Shape`s). Source columns are
 * CONSTRUCTED as `prefix + suffix` via `resolve`, never matched by
 * `startsWith` — the only collision-safe way to split a scenario-prefixed
 * source like `OptimizedRates_Network_String_Agent`.
 */

import type { CogniteClient } from '@cognite/sdk';

import type { DataModelView } from '#core/lib/cognite/view.ts';
import { instancesToColumns, type MintSeriesSource, nodeIdentity } from '#core/lib/cognite/view.ts';
import type { AstInput } from '#core/lib/expr/normalize.ts';
import { KNOWLEDGE_GRAPH_GROUP } from '#core/lib/expr/providers/groups.ts';
import type { DatasetProvider } from '#core/lib/expr/providers/types.ts';
import { projectSchema, type Schema, type ViewDescription } from '#core/lib/expr/schema/types.ts';
import { MaterializedDataset } from '#core/lib/expr/value.ts';

import { type AncestorScopedAssembleArgs, makeAncestorScopedProvider } from './ancestorScope.ts';
import { assembleCanonicalColumns, resolveColumnSet, sourceColumnsOf } from './resolveColumns.ts';
import type { EnrichGroup, Shape } from './shapes/index.ts';

/** A built virtual view: its agent-facing name and the provider that backs it. */
export type VirtualView = {
  readonly name: string;
  readonly provider: DatasetProvider;
};
/**
 * The provider shell every virtual-view factory shares: the dispatch id
 * (`virtual-view:${name}`), the KNOWLEDGE_GRAPH routing group, the
 * single-name `claims` guard, the constant `schema`, and the
 * name-guarded `describe`. Each factory spreads this and adds its own
 * `rewrite` (reshape) or `collapseRef`/`plan`/`fetch` (enriched/flattened).
 */
export const makeVirtualViewProviderShell = ({
  name,
  schema,
  viewDoc,
}: {
  readonly name: string;
  readonly schema: Schema;
  readonly viewDoc: ViewDescription;
}): Pick<DatasetProvider, 'id' | 'group' | 'claims' | 'schema' | 'describe'> => ({
  id: `virtual-view:${name}`,
  group: KNOWLEDGE_GRAPH_GROUP,
  claims: (n) => n === name,
  schema: () => schema,
  describe: (n) => (n === name ? viewDoc : undefined),
});

/** Declarative spec for a reshape view. */
export type ReshapedViewSpec = {
  /** Agent-facing registry name (distinct from the source externalId). */
  readonly name: string;
  /** View-level description shown to the agent by the DescribeViews tool. */
  readonly description: string;
  /** The source CDF view this reshapes. */
  readonly source: DataModelView;
  /** A fixed predicate body over `%`, folded into the expansion and pushed (e.g. `eq('entityType', 'well')`). */
  readonly where?: AstInput;
  /** Passthrough columns kept under their source names (identity, relations, category). */
  readonly keep: Shape;
  /** Prefixed/renamed column groups (the de-prefixed measures). */
  readonly groups?: readonly EnrichGroup[];
  /** CDF client the scoped source read runs through. */
  readonly client: CogniteClient;
  /** Session timeseries minter every per-row ts-ref cell is built through. */
  readonly mintSeries?: MintSeriesSource;
};

export const createReshapedView = (spec: ReshapedViewSpec): VirtualView => {
  // SSOT for de-prefix/rename/typing/docs/units — types from `viewToSchema`,
  // docs from `columnsFromView`, descriptions overridden by the shape's
  // canonical one. Shared verbatim with the enrich/flatten factories.
  const { mintSeries } = spec;
  if (!mintSeries) {
    throw new Error('mintSeries required to mint per-row view timeseries cells');
  }
  const { cols, declared, columns, mintSchema } = resolveColumnSet(spec.source, spec.keep, spec.groups);
  const viewDoc: ViewDescription = { description: spec.description, columns };
  const sourceColumns = sourceColumnsOf(cols);

  // Self-minting scoped provider (no ancestry: `relations: []`). The shared
  // pushdown hooks still fold the agent's filter (canonical → source) AND the
  // fixed `where` into the scoped source read server-side — the same
  // bidirectional rename the old AST `rewrite` did by hand. We mint the rows
  // OURSELVES against `mintSchema` so ts-ref value cells are BOXED with their
  // slug unit (the source schema types the inner value `unknown`, minting
  // unit-less cells that drop the unit through downstream time-bucket/unnest),
  // then de-prefix source → canonical.
  return makeAncestorScopedProvider({
    name: spec.name,
    declared,
    viewDoc,
    source: spec.source,
    cols,
    sourceColumns,
    relations: [],
    enrich: undefined,
    client: spec.client,
    mintSeries,
    where: spec.where,
    assemble: async ({ native, rows }: AncestorScopedAssembleArgs): Promise<MaterializedDataset> => {
      const projected = instancesToColumns(rows, projectSchema(mintSchema, native.columns), spec.source, mintSeries);
      const canonical = assembleCanonicalColumns(cols, projected);
      return new MaterializedDataset(canonical, declared, rows.map(nodeIdentity));
    },
  });
};
